import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Pause, Volume2 } from 'lucide-react';

interface AudioPlayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  audioSrc: string;
  title?: string;
}

const AudioPlayerModal: React.FC<AudioPlayerModalProps> = ({
  isOpen,
  onClose,
  audioSrc,
  title = "AI Virtual Host Demo"
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();

  // Format time as mm:ss
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Handle play/pause
  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Handle seeking
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  // Update time as audio plays
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => {
      setCurrentTime(audio.currentTime);
    };

    const updateDuration = () => {
      if (audio.duration && !isNaN(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    const handleEnded = () => setIsPlaying(false);

    const handleCanPlay = () => {
      // Also try to get duration when audio can play
      if (audio.duration && !isNaN(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('durationchange', updateDuration);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('ended', handleEnded);

    // Check if duration is already available
    if (audio.duration && !isNaN(audio.duration)) {
      setDuration(audio.duration);
    }

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('durationchange', updateDuration);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [isOpen]);

  // Animated waveform visualization
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const bars = 50;
    const barWidth = canvas.width / bars - 2;

    const drawWaveform = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < bars; i++) {
        // Create dynamic height based on position and time
        const baseHeight = Math.sin(i * 0.3) * 20 + 30;
        const animatedHeight = isPlaying
          ? baseHeight + Math.sin(Date.now() * 0.005 + i * 0.5) * 15 + Math.random() * 10
          : baseHeight;

        const x = i * (barWidth + 2);
        const height = Math.max(5, animatedHeight);
        const y = (canvas.height - height) / 2;

        // Gradient color based on progress
        const progress = currentTime / (duration || 1);
        const barProgress = i / bars;

        if (barProgress <= progress) {
          // Played portion - yellow gradient
          const gradient = ctx.createLinearGradient(x, y, x, y + height);
          gradient.addColorStop(0, '#FDB913');
          gradient.addColorStop(1, '#F59E0B');
          ctx.fillStyle = gradient;
        } else {
          // Unplayed portion - blue/gray
          ctx.fillStyle = 'rgba(67, 172, 255, 0.4)';
        }

        ctx.beginPath();
        ctx.roundRect(x, y, barWidth, height, 2);
        ctx.fill();
      }

      animationRef.current = requestAnimationFrame(drawWaveform);
    };

    drawWaveform();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, currentTime, duration]);

  // Auto-play when modal opens
  useEffect(() => {
    if (isOpen && audioRef.current) {
      const audio = audioRef.current;

      // Wait for audio to be ready before playing
      const playAudio = async () => {
        try {
          // Load the audio first
          audio.load();
          await audio.play();
          setIsPlaying(true);

          // Update duration once loaded
          if (audio.duration && !isNaN(audio.duration)) {
            setDuration(audio.duration);
          }
        } catch (err) {
          console.log('Auto-play failed:', err);
        }
      };

      playAudio();
    }
  }, [isOpen]);

  // Stop audio when modal closes
  useEffect(() => {
    if (!isOpen && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setCurrentTime(0);
    }
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative w-full max-w-lg bg-gradient-to-br from-[#1a1f4e] to-[#0f1235] rounded-2xl p-6 shadow-2xl border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-dineai-yellow/20 text-dineai-yellow px-3 py-1 rounded-full text-sm font-medium mb-3">
                <Volume2 className="w-4 h-4" />
                Live Demo Recording
              </div>
              <h3 className="text-xl font-bold text-white">{title}</h3>
              <p className="text-white/60 text-sm mt-1">Listen to Sarah take a real order</p>
            </div>

            {/* Waveform visualization */}
            <div className="bg-white/5 rounded-xl p-4 mb-4">
              <canvas
                ref={canvasRef}
                width={400}
                height={80}
                className="w-full h-20"
              />
            </div>

            {/* Progress bar */}
            <div className="mb-4">
              <input
                type="range"
                min={0}
                max={duration || 100}
                value={currentTime}
                onChange={handleSeek}
                className="w-full h-2 bg-white/10 rounded-full appearance-none cursor-pointer
                  [&::-webkit-slider-thumb]:appearance-none
                  [&::-webkit-slider-thumb]:w-4
                  [&::-webkit-slider-thumb]:h-4
                  [&::-webkit-slider-thumb]:bg-dineai-yellow
                  [&::-webkit-slider-thumb]:rounded-full
                  [&::-webkit-slider-thumb]:cursor-pointer
                  [&::-webkit-slider-thumb]:shadow-lg"
                style={{
                  background: `linear-gradient(to right, #FDB913 ${(currentTime / (duration || 1)) * 100}%, rgba(255,255,255,0.1) ${(currentTime / (duration || 1)) * 100}%)`
                }}
              />
              <div className="flex justify-between text-white/60 text-sm mt-1">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Play/Pause button */}
            <div className="flex justify-center">
              <button
                onClick={togglePlay}
                className="flex items-center gap-3 bg-dineai-yellow hover:bg-dineai-yellow/90 text-black font-bold py-4 px-8 rounded-full transition-all transform hover:scale-105 shadow-lg"
              >
                {isPlaying ? (
                  <>
                    <Pause className="w-6 h-6" />
                    Pause
                  </>
                ) : (
                  <>
                    <Play className="w-6 h-6" />
                    Play
                  </>
                )}
              </button>
            </div>

            {/* Hidden audio element */}
            <audio ref={audioRef} src={audioSrc} preload="auto" />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AudioPlayerModal;
